import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  ArrowLeft,
  Brain,
  BarChart3,
  TrendingUp,
  Calendar,
  Target,
  Activity,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { SurveyData, HealthMetrics } from '../types';

interface StatisticsProps {
  surveyData: SurveyData[];
  healthMetrics: HealthMetrics;
}

const Statistics: React.FC<StatisticsProps> = ({ surveyData, healthMetrics }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const tabs = ['Overview', 'Trends', 'Detailed Analysis', 'Raw Data'];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Calculate statistics
  const calculateStats = () => {
    if (surveyData.length === 0) return null;

    const metrics = ['stressLevel', 'energyLevel', 'sleepQuality', 'physicalActivity', 'nutritionScore', 'hydrationLevel', 'moodScore', 'focusLevel'];
    
    const stats = metrics.reduce((acc, metric) => {
      const values = surveyData.map(s => s[metric as keyof SurveyData] as number);
      acc[metric] = {
        average: Math.round(values.reduce((a, b) => a + b, 0) / values.length),
        min: Math.min(...values),
        max: Math.max(...values),
        trend: values.length > 1 ? (values[values.length - 1] - values[0]) / values.length : 0,
      };
      return acc;
    }, {} as any);

    return stats;
  };

  const stats = calculateStats();

  const chartData = surveyData.slice(-30).map(survey => ({
    date: new Date(survey.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    stress: survey.stressLevel,
    energy: survey.energyLevel,
    sleep: survey.sleepQuality,
    mood: survey.moodScore,
    activity: survey.physicalActivity,
    nutrition: survey.nutritionScore,
    hydration: survey.hydrationLevel,
    focus: survey.focusLevel,
  }));

  const supplementData = surveyData.slice(-30).flatMap(s => s.supplements).reduce((acc, supplement) => {
    acc[supplement] = (acc[supplement] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const exerciseData = surveyData.slice(-30)
    .map(s => s.exerciseType)
    .filter(ex => ex && ex !== 'None')
    .reduce((acc, exercise) => {
      acc[exercise] = (acc[exercise] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

  const pieColors = ['#00ff88', '#4fc3f7', '#ffd700', '#ff6b6b', '#9c27b0', '#ff9800'];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box sx={{ 
          backgroundColor: 'rgba(26, 26, 26, 0.95)', 
          border: '1px solid #00ff88', 
          borderRadius: 2, 
          p: 2 
        }}>
          <Typography variant="body2" sx={{ color: '#00ff88', mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography key={index} variant="caption" sx={{ color: entry.color, display: 'block' }}>
              {entry.name}: {entry.value}%
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  const renderOverview = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ color: '#00ff88' }}>
          Overall Performance Summary
        </Typography>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#00ff88' }}>
              Key Metrics
            </Typography>
            {stats && Object.entries(stats).map(([metric, data]: [string, any]) => (
              <Box key={metric} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {metric.replace(/([A-Z])/g, ' $1').trim()}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ color: '#00ff88' }}>
                      {data.average}%
                    </Typography>
                    {data.trend > 0 ? (
                      <TrendingUp size={16} color="#00ff88" />
                    ) : data.trend < 0 ? (
                      <TrendingUp size={16} color="#ff6b6b" style={{ transform: 'rotate(180deg)' }} />
                    ) : null}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#666' }}>
                  <span>Min: {data.min}%</span>
                  <span>Max: {data.max}%</span>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#00ff88' }}>
              Survey Statistics
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Total Surveys:</Typography>
                <Typography variant="h6" sx={{ color: '#00ff88' }}>
                  {surveyData.length}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Days Tracked:</Typography>
                <Typography variant="h6" sx={{ color: '#00ff88' }}>
                  {surveyData.length > 0 ? 
                    Math.ceil((new Date().getTime() - new Date(surveyData[0].date).getTime()) / (1000 * 60 * 60 * 24)) : 0
                  }
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Average Score:</Typography>
                <Typography variant="h6" sx={{ color: '#00ff88' }}>
                  {healthMetrics.overallScore}%
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Consistency:</Typography>
                <Typography variant="h6" sx={{ color: '#00ff88' }}>
                  {surveyData.length > 0 ? 
                    Math.round((surveyData.length / Math.max(1, Math.ceil((new Date().getTime() - new Date(surveyData[0].date).getTime()) / (1000 * 60 * 60 * 24)))) * 100) : 0
                  }%
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderTrends = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ color: '#00ff88' }}>
          Health Trends (Last 30 Days)
        </Typography>
      </Grid>
      
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2, color: '#fff' }}>
              All Metrics Over Time
            </Typography>
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#fff"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="#fff"
                    fontSize={12}
                    domain={[0, 100]}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="stress" stroke="#ff6b6b" strokeWidth={2} />
                  <Line type="monotone" dataKey="energy" stroke="#ffd700" strokeWidth={2} />
                  <Line type="monotone" dataKey="sleep" stroke="#4fc3f7" strokeWidth={2} />
                  <Line type="monotone" dataKey="mood" stroke="#00ff88" strokeWidth={2} />
                  <Line type="monotone" dataKey="activity" stroke="#9c27b0" strokeWidth={2} />
                  <Line type="monotone" dataKey="nutrition" stroke="#ff9800" strokeWidth={2} />
                  <Line type="monotone" dataKey="hydration" stroke="#00bcd4" strokeWidth={2} />
                  <Line type="monotone" dataKey="focus" stroke="#e91e63" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2, color: '#fff' }}>
              Supplements Usage
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={Object.entries(supplementData).map(([name, value], index) => ({
                      name,
                      value,
                      color: pieColors[index % pieColors.length]
                    }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {Object.entries(supplementData).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2, color: '#fff' }}>
              Exercise Types
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={Object.entries(exerciseData).map(([name, value]) => ({ name, value }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="name" stroke="#fff" fontSize={12} />
                  <YAxis stroke="#fff" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="#00ff88" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderDetailedAnalysis = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ color: '#00ff88' }}>
          Detailed Analysis
        </Typography>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2, color: '#fff' }}>
              Correlation Analysis
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {stats && Object.entries(stats).map(([metric, data]: [string, any]) => (
                <Box key={metric}>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize', mb: 1 }}>
                    {metric.replace(/([A-Z])/g, ' $1').trim()}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                    <span>Average: {data.average}%</span>
                    <span>Range: {data.min}% - {data.max}%</span>
                    <span>Trend: {data.trend > 0 ? '+' : ''}{data.trend.toFixed(1)}%</span>
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2, color: '#fff' }}>
              Performance Insights
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Best Performing Metric
                </Typography>
                <Typography variant="h6" sx={{ color: '#00ff88' }}>
                  {stats ? Object.entries(stats).reduce((best, [metric, data]: [string, any]) => 
                    data.average > best.average ? { metric, ...data } : best
                  , { metric: '', average: 0 }).metric.replace(/([A-Z])/g, ' $1').trim() : 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Needs Improvement
                </Typography>
                <Typography variant="h6" sx={{ color: '#ff6b6b' }}>
                  {stats ? Object.entries(stats).reduce((worst, [metric, data]: [string, any]) => 
                    data.average < worst.average ? { metric, ...data } : worst
                  , { metric: '', average: 100 }).metric.replace(/([A-Z])/g, ' $1').trim() : 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Most Consistent
                </Typography>
                <Typography variant="h6" sx={{ color: '#4fc3f7' }}>
                  {stats ? Object.entries(stats).reduce((most, [metric, data]: [string, any]) => 
                    (data.max - data.min) < (most.max - most.min) ? { metric, ...data } : most
                  , { metric: '', max: 100, min: 0 }).metric.replace(/([A-Z])/g, ' $1').trim() : 'N/A'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderRawData = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ color: '#00ff88' }}>
          Raw Survey Data
        </Typography>
      </Grid>
      
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(26, 26, 26, 0.8)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#00ff88', fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ color: '#00ff88', fontWeight: 600 }}>Stress</TableCell>
                    <TableCell sx={{ color: '#00ff88', fontWeight: 600 }}>Energy</TableCell>
                    <TableCell sx={{ color: '#00ff88', fontWeight: 600 }}>Sleep</TableCell>
                    <TableCell sx={{ color: '#00ff88', fontWeight: 600 }}>Mood</TableCell>
                    <TableCell sx={{ color: '#00ff88', fontWeight: 600 }}>Activity</TableCell>
                    <TableCell sx={{ color: '#00ff88', fontWeight: 600 }}>Nutrition</TableCell>
                    <TableCell sx={{ color: '#00ff88', fontWeight: 600 }}>Hydration</TableCell>
                    <TableCell sx={{ color: '#00ff88', fontWeight: 600 }}>Focus</TableCell>
                    <TableCell sx={{ color: '#00ff88', fontWeight: 600 }}>Exercise</TableCell>
                    <TableCell sx={{ color: '#00ff88', fontWeight: 600 }}>Supplements</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {surveyData.slice(-20).reverse().map((survey) => (
                    <TableRow key={survey.id}>
                      <TableCell sx={{ color: '#fff' }}>
                        {new Date(survey.date).toLocaleDateString()}
                      </TableCell>
                      <TableCell sx={{ color: '#fff' }}>{survey.stressLevel}%</TableCell>
                      <TableCell sx={{ color: '#fff' }}>{survey.energyLevel}%</TableCell>
                      <TableCell sx={{ color: '#fff' }}>{survey.sleepQuality}%</TableCell>
                      <TableCell sx={{ color: '#fff' }}>{survey.moodScore}%</TableCell>
                      <TableCell sx={{ color: '#fff' }}>{survey.physicalActivity}%</TableCell>
                      <TableCell sx={{ color: '#fff' }}>{survey.nutritionScore}%</TableCell>
                      <TableCell sx={{ color: '#fff' }}>{survey.hydrationLevel}%</TableCell>
                      <TableCell sx={{ color: '#fff' }}>{survey.focusLevel}%</TableCell>
                      <TableCell sx={{ color: '#fff' }}>{survey.exerciseType}</TableCell>
                      <TableCell sx={{ color: '#fff' }}>
                        {survey.supplements.join(', ') || 'None'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)' }}>
      <AppBar position="static" sx={{ background: 'rgba(26, 26, 26, 0.95)', backdropFilter: 'blur(10px)' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/')}>
            <ArrowLeft />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <BarChart3 size={32} color="#00ff88" />
            <Typography variant="h6" sx={{ ml: 2, fontWeight: 700 }}>
              PeakState Analytics
            </Typography>
          </Box>
          <Chip
            label={`${surveyData.length} Surveys`}
            sx={{
              backgroundColor: '#00ff88',
              color: '#000',
              fontWeight: 600,
            }}
          />
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ mb: 4 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  color: '#fff',
                  '&.Mui-selected': {
                    color: '#00ff88',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#00ff88',
                },
              }}
            >
              {tabs.map((tab) => (
                <Tab key={tab} label={tab} />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ mt: 3 }}>
            {activeTab === 0 && renderOverview()}
            {activeTab === 1 && renderTrends()}
            {activeTab === 2 && renderDetailedAnalysis()}
            {activeTab === 3 && renderRawData()}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Statistics;
